export class ConfigReport {
	static #hookCharacter = "┗━";

	static Run() {
		let failures = this.#GetFailures(),
			logs = [],
			count = 0;

		Object.entries(failures).forEach(([failure, section], ix) => {
			if (ix !== 0) { logs.push("\n".repeat(2)); }

			logs.push(failure);

			Object.entries(section).forEach(([problem, sectionData], ix) => {
				logs.push(`${"\n".repeat(ix === 0 ? 1 : 2)}${" ".repeat(2)}${this.#hookCharacter} ${problem}`);
				logs.push(`\n${" ".repeat(7)}${this.#hookCharacter} ${sectionData[0].description.trim().replace(/\t/g, "").replace(/\n/g, "\n" + " ".repeat(10))}\n\n`);
				logs.push(`${" ".repeat(9)}Element${sectionData.length === 1 ? "" : "s"}:\n`);

				sectionData.forEach((data, ix) => {
					logs.push(`${" ".repeat(6)}`);
					logs.push(data.element);

					if (ix !== sectionData.length - 1) {
						logs.push("\n");
					}
				});

				count += sectionData.length;
			});
		});

		if (logs.length) {
			Toast.ShowWarning(`Auxilium has identified ${count} resolvable issue${count === 1 ? "" : "s"} on this page. Please check the developer console to view more details.`);

			logs.unshift(`Total Issues: ${count}\n\n`);
			logs.unshift(`Sections: ${Object.keys(failures).length}\n`);
			logs.unshift("font-size:1.75rem;font-weight:700;line-height:2.8571rem;");
			logs.unshift("%c🠶 Auxilium Report\n");

			console.log(...logs);
		}
	}

	static #GetFailures() {
		let failures = [];

		if (!document.documentElement.dataset.serverPath) {
			failures.push({
				section: "Auxilium",
				type: "config",
				problem: "Missing HTML server-path",
				description: `The root HTML element in DOM needs the server-path attribute so that Auxilium can provide more accurate
											results when parsing relative URLs.
											`,
				element: document.documentElement
			});
		}

		if (!document.documentElement.lang) {
			failures.push({
				section: "Auxilium",
				type: "accessibility",
				problem: "Missing HTML lang",
				description: `The root HTML element in DOM should be given a lang value, for example lang="en". This ensures screen
				readers read/pronounce page content as intended.
											`,
				element: document.documentElement
			});
		}

		for (const breadcrumb of document.querySelectorAll("chip-breadcrumb")) {
			if (!breadcrumb.description && !breadcrumb.getAttribute("description")) {
				failures.push({
					section: "Breadcrumbs",
					type: "accessibility",
					problem: "Breadcrumb has no accessible name",
					description: `Provide a meaningful, accessible description via the description attribute to help screen readers.`,
					element: breadcrumb
				});
			}
		}

		for (const tree of document.querySelectorAll("chip-tree")) {
			if (!tree.description && !tree.getAttribute("description")) {
				failures.push({
					section: "Tree Views",
					type: "accessibility",
					problem: "Tree view has no accessible name",
					description: `Provide a meaningful, accessible description via the description attribute to help screen readers.`,
					element: tree
				});
			}
		}

		for (const button of document.querySelectorAll("chip-button")) {
			if (button.buttonStyle === "icon"
				&& (!button.description && !button.label && !button.tooltip)
				&& (!button.id || !document.querySelector(`[for='${button.id}']`))) {

				failures.push({
					section: "Buttons",
					type: "accessibility",
					problem: "Icon button has has no accessible name",
					description: `
						Provide either a label or tooltip to give extra information, especially to buttons that hold no discernible
						text. If you'd like to avoid displaying visible text on the page, you can supply a description instead.
					`,
					element: button
				});
			}
		}

		for (const iconPicker of document.querySelectorAll("chip-iconpicker")) {
			if (!iconPicker.label && !iconPicker.getAttribute("label") && !iconPicker.description && !iconPicker.getAttribute("description")) {
				failures.push({
					section: "Icon Pickers",
					type: "accessibility",
					problem: "Icon picker has no accessible name",
					description: `Provide a meaningful, accessible label via the label attribute to help screen readers. If you'd like
					to avoid displaying visible text on the page, you can supply a description instead.`,
					element: iconPicker
				});
			}
		}

		failures.push(...this.#GetInputFailures());
		failures.push(...this.#GetTextareaFailures());

		for (const img of document.querySelectorAll("img")) {
			if (!img.hasAttribute("alt") && !img.getAttribute("aria-label") && !img.getAttribute("aria-labelledby") && img.role !== "presentation") {
				failures.push({
					section: "Images",
					type: "accessibility",
					problem: "Image has neither an accessible name nor is marked as decorative",
					description: `If the image conveys meaning, provide either an alt, aria-label or aria-labelledby attribute. If not, you
					can pass role="presentation" to mark the image as decorative.`,
					element: img
				});
			}
		}

		for (const strandedHeader of document.querySelectorAll("h1:not(chip-header h1),h2:not(chip-header h2),h3:not(chip-header h3),h4:not(chip-header h4),h5:not(chip-header h5),h6:not(chip-header h6)")) {
			failures.push({
				section: "Stranded Elements",
				type: "stranded",
				problem: "Header does not reside within a chip-header",
				description: `When using Auxilium, it is required to use a chip-header component when working with headers to fully
				abuse Auxilium's styling and capabilities.`,
				element: strandedHeader
			});
		}

		failures = GroupBy(failures, x => x.section);

		for (const failure in failures) {
			failures[failure] = GroupBy(failures[failure], x => x.problem);
		}

		return failures;
	}

	static #GetTextareaFailures() {
		const failures = [];

		for (const textArea of document.querySelectorAll("chip-textarea")) {
			if (!textArea.description && !textArea.label && !textArea.placeholder && !textArea.getAttribute("label") && !textArea.getAttribute("description") && !textArea.getAttribute("placeholder") && !document.querySelector(`label[for='${textArea.identifier}']`)) {
				failures.push({
					section: "Textareas",
					type: "accessibility",
					problem: "Textarea has no accessible name",
					description: `Each chip-textarea needs to have a label (or alternatively, a placeholder) in order to provide meaningful descriptions, especially to
					screen readers. If you'd like to avoid displaying visible text on page, you can supply a description instead.`,
					element: textArea
				});
			}
		}

		return failures;
	}

	static #GetInputFailures() {
		const failures = [];

		for (const input of document.querySelectorAll("chip-input")) {
			if (!input.description && !input.label && !input.placeholder && !input.getAttribute("label") && !input.getAttribute("description") && !input.getAttribute("placeholder") && !document.querySelector(`label[for='${input.identifier}']`)) {
				failures.push({
					section: "Inputs",
					type: "accessibility",
					problem: "Input has no accessible name",
					description: `Each chip-input needs to have a label (or alternatively, a placeholder) in order to provide meaningful descriptions, especially to
					screen readers. If you'd like to avoid displaying visible text on page, you can supply a description instead.`,
					element: input
				});
			}
		}

		return failures;
	}
}